import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import DesktopLayout from '../../../layout/DesktopLayout';
import BottomNav from '../../../BottomNav';
import { useDesktopTopBar } from '../../../../context/DesktopLayoutContext';
import { useIsDesktop } from '../../../reports/reportTokens';
import { useBranch } from '../../../../context/BranchContext';
import { useMediaLibraryStore } from '../../../../redux/display/mediaLibrary/mediaLibraryHooks';
import { usePlaylistStore } from '../../../../redux/display/playlist/playlistHooks';
import { useFormDiscardGuard } from '../../../productCatalog/hooks/useFormDiscardGuard';
import { DeleteConfirmationModal } from '../../components/ui/DeleteConfirmationModal/DeleteConfirmationModal';
import { useDisplayStore, useDisplayToastStore } from '../../../../redux/display/display/displayHooks';
import { buildDisplayMediaSrc } from '../../../../services/displayApi';
import { Icon } from '../../components/ui/Icon/Icon';
import { DisplayToastContainer } from '../../components/ui/DisplayToastContainer/DisplayToastContainer';
import { OrientationSegmented } from '../../components/ui/OrientationSegmented/OrientationSegmented';
import { MediaCard } from '../../components/data/MediaCard/MediaCard';
import MediaScheduler from '../../components/forms/MediaScheduler/MediaScheduler';
import { displayPaths } from '../../paths';
import type { ContentAssignmentType, DisplayItem, DisplayOrientation, MediaLibraryItem, Playlist, ScheduleSlot } from '../../data/types';
import './CreateDisplay.css';

const BORDER = 'var(--card-border, #E2E5EC)';
const T1 = 'var(--t1, #1A1D2E)';
const T2 = 'var(--t2, #475569)';
const T3 = 'var(--t3, #9CA3B8)';
const ACCENT = 'var(--accent, #D23A3B)';
const ACCENT_BG = 'var(--accent-soft, rgba(210,58,59,0.08))';
const RED = 'var(--red, #DC2626)';
const GREEN = 'var(--green, #16A34A)';
const FONT = 'var(--font-primary, DM Sans, sans-serif)';

const cardStyle: React.CSSProperties = { background: 'var(--surface, #fff)', border: `1px solid ${BORDER}`, borderRadius: 14, padding: 20, boxShadow: 'var(--card-shadow, 0 1px 4px rgba(0,0,0,0.05))' };
const inputStyle: React.CSSProperties = { width: '100%', padding: '10px 14px', borderRadius: 10, border: `1px solid ${BORDER}`, fontSize: 13, fontFamily: FONT, color: T1, outline: 'none', boxSizing: 'border-box' };

let sectionCounter = 0;
function nextSectionId() { sectionCounter += 1; return `section-${sectionCounter}`; }

interface SectionDraft {
  id: string;
  orientation: DisplayOrientation | null;
  contentType: ContentAssignmentType | null;
  mediaId: string | null;
  playlistId: string | null;
  fallbackMediaId: string | null;
  /** Each media item keeps its own schedule, keyed by media id, so switching selection never leaks or duplicates another media's slots. */
  schedules: Record<string, ScheduleSlot[]>;
  displayTime: string;
}

function emptySection(): SectionDraft {
  return { id: nextSectionId(), orientation: null, contentType: null, mediaId: null, playlistId: null, fallbackMediaId: null, schedules: {}, displayTime: '15' };
}

function ContentTypeChoice({ value, onChange, disabled }: { value: ContentAssignmentType | null; onChange: (v: ContentAssignmentType) => void; disabled?: boolean }) {
  const options: { key: ContentAssignmentType; label: string; icon: string }[] = [
    { key: 'single', label: 'Single Media', icon: 'image' },
    { key: 'playlist', label: 'Playlist', icon: 'playlist_play' },
  ];
  return (
    <div style={{ display: 'flex', gap: 12 }}>
      {options.map((opt) => {
        const active = value === opt.key;
        return (
          <button
            key={opt.key}
            type="button"
            disabled={disabled}
            onClick={() => onChange(opt.key)}
            style={{
              flex: 1, display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 10,
              padding: '14px 16px', borderRadius: 12, cursor: disabled ? 'not-allowed' : 'pointer',
              border: `1.5px solid ${active ? ACCENT : BORDER}`, background: active ? ACCENT_BG : 'var(--surface, #fff)',
              opacity: disabled ? 0.6 : 1, transition: 'all .15s',
            }}
          >
            <Icon name={opt.icon} size={18} color={active ? ACCENT : T3} />
            <span style={{ fontSize: 13, fontWeight: 600, color: active ? ACCENT : T2, fontFamily: FONT }}>{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/** Thumbnail strip resolving the playlist's first few media entries — mirrors Media Library's PlaylistCard. */
function PlaylistThumbStrip({ playlist, mediaItems }: { playlist: Playlist; mediaItems: MediaLibraryItem[] }) {
  const resolved = useMemo(
    () => [...playlist.mediaEntries]
      .sort((a, b) => a.sequenceNo - b.sequenceNo)
      .map((entry) => mediaItems.find((m) => m.id === entry.mediaId))
      .filter((m): m is MediaLibraryItem => Boolean(m)),
    [playlist.mediaEntries, mediaItems],
  );
  const visible = resolved.slice(0, 3);
  const overflow = resolved.length - visible.length;

  if (visible.length === 0) {
    return (
      <div style={{ aspectRatio: '16 / 8', borderRadius: 10, background: 'var(--hover, #F0F1F5)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px solid ${BORDER}` }}>
        <Icon name="perm_media" size={22} color={T3} />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 4, aspectRatio: '16 / 8' }}>
      {visible.map((item, i) => (
        <div key={item.id} style={{ position: 'relative', flex: 1, borderRadius: 8, overflow: 'hidden', background: 'linear-gradient(180deg, #172033 0%, #101827 100%)' }}>
          {item.kind === 'video' ? (
            <video src={buildDisplayMediaSrc(item.id, item.mimeType) ?? item.url} muted preload="metadata" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <img src={buildDisplayMediaSrc(item.id, item.mimeType) ?? item.url} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          )}
          {i === visible.length - 1 && overflow > 0 && (
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(17,24,39,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', fontFamily: FONT }}>+{overflow}</span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function PlaylistPickCard({ playlist, mediaItems, selected, onClick }: { playlist: Playlist; mediaItems: MediaLibraryItem[]; selected: boolean; onClick: () => void }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: 'var(--surface, #fff)', borderRadius: 14, overflow: 'hidden', cursor: 'pointer',
        border: `1.5px solid ${selected ? ACCENT : BORDER}`,
        boxShadow: selected ? '0 0 0 3px var(--accent-soft, rgba(210,58,59,0.12))' : 'var(--card-shadow, 0 1px 4px rgba(0,0,0,0.05))',
        transition: 'box-shadow .15s, border-color .15s',
      }}
    >
      <div style={{ position: 'relative', padding: '12px 12px 0' }}>
        <PlaylistThumbStrip playlist={playlist} mediaItems={mediaItems} />
        <div style={{
          position: 'absolute', top: 20, right: 20, width: 17, height: 17, borderRadius: 4,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: selected ? ACCENT : 'rgba(17,24,39,0.55)', border: selected ? 'none' : '1.5px solid rgba(255,255,255,0.7)',
        }}>
          {selected && <Icon name="check" size={11} color="#fff" />}
        </div>
      </div>
      <div style={{ padding: '10px 12px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
          <div style={{ width: 22, height: 22, borderRadius: 7, background: `${ACCENT}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon name="playlist_play" size={12} color={ACCENT} />
          </div>
          <div style={{ fontSize: 13, fontWeight: 700, color: T1, fontFamily: FONT, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={playlist.name}>
            {playlist.name}
          </div>
        </div>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: T3, fontFamily: FONT }}>
          <Icon name="perm_media" size={12} color={T3} />
          {playlist.mediaEntries.length} media item{playlist.mediaEntries.length === 1 ? '' : 's'}
        </span>
      </div>
    </div>
  );
}

/** Left column: compact setup controls — orientation + content type. Locked once a display exists,
 *  since switching mode/orientation after creation would invalidate the already-selected media/playlist. */
function SetupCard({ section, onChange, disabled }: { section: SectionDraft; onChange: (next: SectionDraft) => void; disabled?: boolean }) {
  return (
    <div style={{ ...cardStyle, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        {disabled && (
          <div style={{ fontSize: 11, color: T2, marginBottom: 8, fontFamily: FONT }}>
            <strong style={{ fontWeight: 700 }}>Note:</strong> Orientation and content type can't be changed after a display is created.
          </div>
        )}
        <div style={{ fontSize: 11, fontWeight: 600, color: T3, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8, fontFamily: FONT }}>Choose Orientation</div>
        <OrientationSegmented
          value={section.orientation}
          onChange={(orientation) => onChange({ ...section, orientation, mediaId: null, playlistId: null })}
          disabled={disabled}
        />
      </div>

      {section.orientation && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: T3, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8, fontFamily: FONT }}>Choose Content Assignment</div>
          <ContentTypeChoice
            value={section.contentType}
            onChange={(contentType) => onChange({ ...section, contentType, mediaId: null, playlistId: null })}
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );
}

/** Right column: media/playlist picker + scheduler — needs the wider space freed up by the left column. */
function AssignmentCard({
  section, mediaItems, playlists, onChange, onScheduleValidityChange,
}: {
  section: SectionDraft;
  mediaItems: MediaLibraryItem[];
  playlists: Playlist[];
  onChange: (next: SectionDraft) => void;
  onScheduleValidityChange: (valid: boolean) => void;
}) {
  const eligibleMedia = useMemo(
    () => (section.orientation ? mediaItems.filter((m) => m.orientation === section.orientation) : []),
    [mediaItems, section.orientation],
  );
  const eligiblePlaylists = useMemo(
    () => (section.orientation ? playlists.filter((p) => p.orientation === section.orientation) : []),
    [playlists, section.orientation],
  );

  const selectedPlaylist = useMemo(
    () => playlists.find((p) => p.id === section.playlistId) ?? null,
    [playlists, section.playlistId],
  );

  const mediaStripRef = useRef<HTMLDivElement>(null);
  const playlistStripRef = useRef<HTMLDivElement>(null);

  // Switching orientation swaps the eligible list entirely. If there's already a selection
  // (editing an existing display), scroll that item into view instead of hiding it at the
  // start of the strip; otherwise jump back to the start.
  useEffect(() => {
    const strip = mediaStripRef.current;
    const selectedCard = section.mediaId ? strip?.querySelector(`[data-item-id="${section.mediaId}"]`) : null;
    if (selectedCard) selectedCard.scrollIntoView({ behavior: 'auto', inline: 'center', block: 'nearest' });
    else strip?.scrollTo({ left: 0 });
  }, [section.orientation, section.mediaId]);

  useEffect(() => {
    const strip = playlistStripRef.current;
    const selectedCard = section.playlistId ? strip?.querySelector(`[data-item-id="${section.playlistId}"]`) : null;
    if (selectedCard) selectedCard.scrollIntoView({ behavior: 'auto', inline: 'center', block: 'nearest' });
    else strip?.scrollTo({ left: 0 });
  }, [section.orientation, section.playlistId]);

  if (!section.orientation || !section.contentType) {
    return (
      <div style={{ ...cardStyle, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '48px 24px', color: T3, textAlign: 'center' }}>
        <Icon name="tune" size={28} color={T3} />
        <div style={{ fontSize: 13, fontWeight: 600, color: T2, fontFamily: FONT }}>Choose an orientation and content type</div>
        <div style={{ fontSize: 12, color: T3, fontFamily: FONT }}>Your media or playlist options will show up here.</div>
      </div>
    );
  }

  return (
    <div style={{ ...cardStyle, display: 'flex', flexDirection: 'column', gap: 16 }}>
      {section.contentType === 'single' && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: T3, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8, fontFamily: FONT }}>
            Select Media ({section.orientation})
          </div>
          {eligibleMedia.length === 0 ? (
            <div style={{ fontSize: 12, color: T3, fontFamily: FONT, padding: '12px 0' }}>
              No {section.orientation.toLowerCase()} media available — upload some from the Media Library first.
            </div>
          ) : (
            <div ref={mediaStripRef} className="display-media-strip" style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 4, scrollbarColor: `${BORDER} transparent`, scrollbarWidth: 'thin' }}>
              <style>{`
                .display-media-strip::-webkit-scrollbar { height: 6px; background: transparent; }
                .display-media-strip::-webkit-scrollbar-track { background: transparent; }
                .display-media-strip::-webkit-scrollbar-thumb { background: ${BORDER}; border-radius: 999px; }
                .display-media-strip::-webkit-scrollbar-button { display: none; width: 0; height: 0; }
              `}</style>
              {eligibleMedia.map((item) => (
                <div key={item.id} data-item-id={item.id} style={{ flex: '0 0 200px', width: 200 }}>
                  <MediaCard
                    item={item}
                    checkable
                    selected={section.mediaId === item.id}
                    onPreview={() => onChange({ ...section, mediaId: section.mediaId === item.id ? null : item.id })}
                  />
                </div>
              ))}
            </div>
          )}
          {section.mediaId && (
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${BORDER}` }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: T3, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8, fontFamily: FONT }}>Scheduler</div>
              <MediaScheduler
                key={section.mediaId}
                schedule={section.schedules[section.mediaId] ?? []}
                onChange={(schedule) => onChange({ ...section, schedules: { ...section.schedules, [section.mediaId as string]: schedule } })}
                onValidityChange={onScheduleValidityChange}
              />
            </div>
          )}
        </div>
      )}

      {section.orientation && section.contentType === 'playlist' && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: T3, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8, fontFamily: FONT }}>
            Select Playlist ({section.orientation})
          </div>
          {eligiblePlaylists.length === 0 ? (
            <div style={{ fontSize: 12, color: T3, fontFamily: FONT, padding: '12px 0' }}>
              No {section.orientation.toLowerCase()} playlists yet — create one from the Media Library first.
            </div>
          ) : (
            <div ref={playlistStripRef} className="display-playlist-strip" style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 4, scrollbarColor: `${BORDER} transparent`, scrollbarWidth: 'thin' }}>
              <style>{`
                .display-playlist-strip::-webkit-scrollbar { height: 6px; background: transparent; }
                .display-playlist-strip::-webkit-scrollbar-track { background: transparent; }
                .display-playlist-strip::-webkit-scrollbar-thumb { background: ${BORDER}; border-radius: 999px; }
                .display-playlist-strip::-webkit-scrollbar-button { display: none; width: 0; height: 0; }
              `}</style>
              {eligiblePlaylists.map((playlist) => (
                <div key={playlist.id} data-item-id={playlist.id} style={{ flex: '0 0 220px', width: 220 }}>
                  <PlaylistPickCard
                    playlist={playlist}
                    mediaItems={mediaItems}
                    selected={section.playlistId === playlist.id}
                    onClick={() => onChange({ ...section, playlistId: section.playlistId === playlist.id ? null : playlist.id })}
                  />
                </div>
              ))}
            </div>
          )}
          {selectedPlaylist && (
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${BORDER}` }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: T3, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8, fontFamily: FONT }}>
                Scheduler
              </div>
              <MediaScheduler
                key={section.playlistId}
                schedule={section.schedules[section.playlistId as string] ?? []}
                onChange={(schedule) => onChange({ ...section, schedules: { ...section.schedules, [section.playlistId as string]: schedule } })}
                onValidityChange={onScheduleValidityChange}
              />
            </div>
          )}
        </div>
      )}

      {/* Fallback Media Section */}
      {section.orientation && (section.mediaId || section.playlistId) && (
        <div style={{ marginTop: 18, paddingTop: 18, borderTop: `1px solid ${BORDER}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: T3, textTransform: 'uppercase', letterSpacing: 0.4, fontFamily: FONT }}>
              Fallback Media ({section.orientation}) <span style={{ color: RED }}>*</span>
            </div>
            <span
              style={{
                fontSize: 10,
                fontWeight: 600,
                padding: '2px 8px',
                borderRadius: 4,
                background: 'var(--amber-soft, #FEF3C7)',
                color: '#D97706',
                fontFamily: FONT,
              }}
            >
              Off-Hours Backup
            </span>
          </div>
          <div style={{ fontSize: 12, color: T2, marginBottom: 12, fontFamily: FONT, lineHeight: 1.4 }}>
            Required: Plays automatically when current time is outside the scheduled window (e.g. before start time or after end time) so your screen is never blank.
          </div>

          {eligibleMedia.length === 0 ? (
            <div style={{ fontSize: 12, color: T3, fontFamily: FONT, padding: '8px 0' }}>
              No {section.orientation.toLowerCase()} media available in library.
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10 }}>
              {eligibleMedia.map((item) => {
                const isSelected = section.fallbackMediaId === item.id;
                return (
                  <div
                    key={`fallback-${item.id}`}
                    onClick={() => onChange({ ...section, fallbackMediaId: isSelected ? null : item.id })}
                    style={{
                      cursor: 'pointer',
                      borderRadius: 10,
                      border: `1px solid ${isSelected ? ACCENT : BORDER}`,
                      background: isSelected ? ACCENT_BG : 'var(--surface, #fff)',
                      padding: 6,
                      transition: 'all .15s ease',
                      position: 'relative',
                    }}
                  >
                    <div style={{ aspectRatio: '16 / 9', borderRadius: 6, overflow: 'hidden', position: 'relative', background: '#000' }}>
                      {item.kind === 'video' ? (
                        <video src={buildDisplayMediaSrc(item.id, item.mimeType) ?? item.url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted />
                      ) : (
                        <img src={buildDisplayMediaSrc(item.id, item.mimeType) ?? item.url} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      )}
                      <div style={{ position: 'absolute', top: 6, left: 6, width: 22, height: 22, borderRadius: '50%', background: 'rgba(17,24,39,0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Icon name={item.kind === 'video' ? 'videocam' : 'image'} size={12} color="#fff" />
                      </div>
                      {isSelected && (
                        <div style={{ position: 'absolute', top: 6, right: 6, width: 20, height: 20, borderRadius: '50%', background: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Icon name="check" size={14} color="#fff" />
                        </div>
                      )}
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: T1, fontFamily: FONT, marginTop: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.name}
                    </div>
                    <div style={{ fontSize: 10, color: isSelected ? ACCENT : T3, fontFamily: FONT, marginTop: 2, fontWeight: isSelected ? 600 : 400 }}>
                      {isSelected ? '✓ Fallback Selected' : 'Set as Fallback'}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function sectionIsComplete(section: SectionDraft) {
  if (!section.orientation || !section.contentType) return false;
  if (section.contentType === 'single' && !section.mediaId) return false;
  if (section.contentType === 'playlist' && !section.playlistId) return false;
  return Boolean(section.fallbackMediaId);
}

export default function CreateDisplay() {
  const navigate = useNavigate();
  const location = useLocation();
  const isDesktop = useIsDesktop();
  const isMobile = !isDesktop;
  const setTopBarProps = useDesktopTopBar();
  const { selectedBranch } = useBranch();
  const { items: mediaItems, fetchMedia } = useMediaLibraryStore();
  const { items: playlists, fetchPlaylists } = usePlaylistStore();
  const { items: displayItems, editingItemId, saveDisplay, saving } = useDisplayStore();
  const { addToast } = useDisplayToastStore();

  const searchParams = new URLSearchParams(location.search);
  const editId = searchParams.get('id') || (location.state as any)?.displayItem?.id || editingItemId;

  const editItem: DisplayItem | null = useMemo(() => {
    if (!editId) return null;
    return (location.state as any)?.displayItem || displayItems.find((d) => d.id === editId) || null;
  }, [editId, location.state, displayItems]);

  const isEdit = Boolean(editItem);

  const [displayName, setDisplayName] = useState('');
  const [section, setSection] = useState<SectionDraft>(() => emptySection());
  const [nameError, setNameError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [scheduleValid, setScheduleValid] = useState(true);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const isFormDirty = useMemo(() => {
    if (isEdit && editItem) {
      const originalName = editItem.displayName || editItem.name || '';
      if (displayName.trim() !== originalName.trim()) return true;

      const attr = editItem.attribute;
      const originalOrientation = attr?.orientation || 'Landscape';
      if (section.orientation !== originalOrientation) return true;

      const originalIsPlaylist = attr?.mode === 'P' || Boolean(attr?.playlistId);
      const currentIsPlaylist = section.contentType === 'playlist';
      if (currentIsPlaylist !== originalIsPlaylist) return true;

      const originalFallbackMediaId = attr?.fallbackMedia?.mediaId ?? null;
      if (section.fallbackMediaId !== originalFallbackMediaId) return true;

      if (currentIsPlaylist) {
        const originalPlaylistId = attr?.playlistId ?? '';
        if (section.playlistId !== originalPlaylistId) return true;
      } else {
        const firstMedia = attr?.spotLightMedia?.[0];
        const originalMediaId = firstMedia?.mediaId ?? null;
        if (section.mediaId !== originalMediaId) return true;

        const originalDisplayTime = firstMedia?.displayTime || '10';
        if (section.displayTime !== originalDisplayTime) return true;
      }

      // Check schedule changes
      const originalTimeSlots = attr?.timeSlots ?? [];
      const currentKey = currentIsPlaylist ? (section.playlistId ?? '') : (section.mediaId ?? '');
      const currentTimeSlots = section.schedules[currentKey] ?? [];
      
      if (originalTimeSlots.length !== currentTimeSlots.length) return true;
      for (let i = 0; i < originalTimeSlots.length; i++) {
        const a = originalTimeSlots[i];
        const b = currentTimeSlots[i];
        if (!b || a.weekday !== b.weekday || a.start_time !== b.start_time || a.end_time !== b.end_time) {
          return true;
        }
      }

      return false;
    } else {
      if (displayName.trim() !== '') return true;
      if (section.orientation !== null) return true;
      if (section.contentType !== null) return true;
      if (section.mediaId !== null) return true;
      if (section.playlistId !== null) return true;
      if (section.fallbackMediaId !== null) return true;
      return false;
    }
  }, [isEdit, editItem, displayName, section]);

  const isDirty = isFormDirty && !saving && !isSubmitted;

  const {
    showDiscardModal,
    handleCancel,
    handleConfirmDiscard,
    handleKeepEditing,
  } = useFormDiscardGuard(isDirty, displayPaths.root);

  const currentLocationId = selectedBranch?.id ?? (selectedBranch as any)?.locationId ?? '';

  const hasInitializedEditRef = useRef(false);

  useEffect(() => {
    setTopBarProps({
      title: isEdit ? 'Edit Display' : 'Create Display',
      breadcrumbs: isEdit ? 'Display › Edit Display' : 'Display › Create Display',
    });
  }, [setTopBarProps, isEdit]);

  useEffect(() => { if (currentLocationId) { fetchMedia(currentLocationId); fetchPlaylists(currentLocationId); } }, [fetchMedia, fetchPlaylists, currentLocationId]);

  useEffect(() => {
    if (editItem && !hasInitializedEditRef.current) {
      hasInitializedEditRef.current = true;
      const dName = editItem.displayName || editItem.name || '';
      setDisplayName(dName);
      // templateType is a cosmetic local field only — the backend's templateType ("MEDIA") is a
      // different concept and always fixed, so this dropdown always stays on its one option.

      const attr = editItem.attribute;
      const orientation: DisplayOrientation = attr?.orientation || 'Landscape';
      const timeSlots: ScheduleSlot[] = attr?.timeSlots ?? [];
      const fallbackMediaId = attr?.fallbackMedia?.mediaId ?? null;
      const isPlaylist = attr?.mode === 'P' || Boolean(attr?.playlistId);

      if (isPlaylist) {
        const pId = attr?.playlistId ?? '';
        setSection({
          id: 'sec-1',
          orientation,
          contentType: 'playlist',
          playlistId: pId,
          mediaId: null,
          fallbackMediaId,
          displayTime: '10',
          schedules: pId ? { [pId]: timeSlots } : {},
        });
      } else {
        const firstMedia = attr?.spotLightMedia?.[0];
        const mId = firstMedia?.mediaId ?? null;
        setSection({
          id: 'sec-1',
          orientation,
          contentType: 'single',
          mediaId: mId,
          playlistId: null,
          fallbackMediaId,
          displayTime: firstMedia?.displayTime || '10',
          schedules: mId ? { [mId]: timeSlots } : {},
        });
      }
    }
  }, [editItem]);

  const handleSubmit = async () => {
    setSubmitError(null);
    let hasError = false;
    if (!displayName.trim()) { setNameError('Display name is required'); hasError = true; } else setNameError(null);
    if (!sectionIsComplete(section)) {
      if (!section.fallbackMediaId) {
        setSubmitError('Fallback media is required — select an off-hours backup media for your display.');
      } else {
        setSubmitError('Choose an orientation and assign a single media or a playlist before creating the display.');
      }
      hasError = true;
    }
    if (!currentLocationId) { setSubmitError('No outlet selected — pick a location first.'); hasError = true; }
    if (!scheduleValid) { setSubmitError('Fix the overlapping time slots before saving.'); hasError = true; }
    if (hasError) return;

    const scheduleKey = section.contentType === 'single' ? section.mediaId : section.playlistId;
    const timeSlots: ScheduleSlot[] = scheduleKey ? (section.schedules[scheduleKey] ?? []) : [];

    try {
      await saveDisplay({
        id: isEdit && editItem ? editItem.id : undefined,
        locationId: currentLocationId,
        displayName: displayName.trim(),
        mode: section.contentType === 'playlist' ? 'P' : 'S',
        mediaId: section.contentType === 'single' ? section.mediaId ?? undefined : undefined,
        playlistId: section.contentType === 'playlist' ? section.playlistId ?? undefined : undefined,
        fallbackMediaId: section.fallbackMediaId as string,
        orientation: section.orientation as DisplayOrientation,
        timeSlots,
      });
      setIsSubmitted(true);
      addToast(isEdit ? 'Display updated successfully' : 'Display created successfully', 'success');
      navigate(displayPaths.root);
    } catch (err: any) {
      setSubmitError(err?.message || `Failed to ${isEdit ? 'update' : 'create'} display`);
      addToast(`Failed to ${isEdit ? 'update' : 'create'} display`, 'error');
    }
  };

  const actionButtons = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {submitError && (
        <div style={{ padding: '10px 14px', borderRadius: 10, background: 'var(--red-soft, rgba(220,38,38,0.08))', border: `1px solid ${RED}`, fontSize: 12, color: RED, fontFamily: FONT }}>
          {submitError}
        </div>
      )}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={handleCancel}
          disabled={saving}
          style={{ padding: '10px 20px', borderRadius: 10, border: `1px solid ${BORDER}`, background: 'var(--surface, #fff)', color: T2, fontSize: 13, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: FONT }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={saving || !scheduleValid}
          title={!scheduleValid ? 'Fix the overlapping time slots before saving' : undefined}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '10px 24px', borderRadius: 10,
            border: 'none', background: ACCENT, color: '#fff',
            opacity: (saving || !scheduleValid) ? 0.5 : 1,
            fontSize: 13, fontWeight: 600, cursor: (saving || !scheduleValid) ? 'not-allowed' : 'pointer', fontFamily: FONT,
            transition: 'opacity .15s ease',
          }}
        >
          {saving && <Icon name="progress_activity" size={15} color="#fff" style={{ animation: 'spin 1s linear infinite' }} />}
          {saving ? (isEdit ? 'Saving…' : 'Creating…') : (isEdit ? 'Save Changes' : 'Create Display')}
        </button>
        <style>{'@keyframes spin { to { transform: rotate(360deg); } }'}</style>
      </div>
    </div>
  );

  const content = (
    <div style={{ padding: isMobile ? '12px 16px 88px' : 28, display: 'flex', flexDirection: 'column', gap: 18, width: '100%', boxSizing: 'border-box' }}>
      {!isMobile && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            type="button"
            onClick={handleCancel}
            aria-label="Go back to Display List"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 8,
              transition: 'background .15s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--hover, #F0F1F5)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
          >
            <Icon name="arrow_back" size={20} color={T2} />
          </button>
          <div>
            <div style={{ fontSize: 20, fontWeight: 700, color: T1, fontFamily: FONT }}>
              {isEdit ? 'Edit Display' : 'Create Display'}
            </div>
            <div style={{ fontSize: 12, color: T3, fontFamily: FONT, marginTop: 2 }}>
              {isEdit ? 'Update display media and settings.' : 'Name your display, choose an orientation, then assign a single media or a playlist.'}
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 480px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 18, order: isMobile ? 1 : 0 }}>
          <AssignmentCard
            section={section}
            mediaItems={mediaItems}
            playlists={playlists}
            onChange={setSection}
            onScheduleValidityChange={setScheduleValid}
          />
        </div>

        <div
          style={{
            flex: isMobile ? '1 1 100%' : '0 0 340px', minWidth: isMobile ? undefined : 300, display: 'flex', flexDirection: 'column', gap: 18,
            position: isMobile ? undefined : 'sticky', top: isMobile ? undefined : 20,
            order: isMobile ? 0 : 1,
          }}
        >
          <div style={{ ...cardStyle, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: T3, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8, fontFamily: FONT }}>
                Display Name <span style={{ color: RED }}>*</span>
              </div>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="e.g. Front Counter Screen"
                style={{ ...inputStyle, border: `1px solid ${nameError ? RED : BORDER}` }}
              />
              {nameError && <div style={{ fontSize: 11, color: RED, marginTop: 6, fontFamily: FONT }}>{nameError}</div>}
            </div>

            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: T3, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8, fontFamily: FONT }}>
                Template Type
              </div>
              <div style={{ ...inputStyle, background: 'var(--hover, #F0F1F5)', color: T2, cursor: 'default' }}>
                Spotlight
              </div>
            </div>
          </div>

          <SetupCard section={section} onChange={setSection} disabled={isEdit} />

          {!isMobile && actionButtons}
        </div>
      </div>

      {isMobile && actionButtons}

      <DeleteConfirmationModal
        open={showDiscardModal}
        onClose={handleKeepEditing}
        onConfirm={handleConfirmDiscard}
        title="Discard changes?"
        description="You have unsaved changes. Are you sure you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
      />

      <DisplayToastContainer />
    </div>
  );

  if (isMobile) {
    return (
      <div style={{ background: 'var(--bg, #F5F6FA)', minHeight: '100vh', fontFamily: FONT, overflowX: 'hidden', width: '100%', boxSizing: 'border-box' }}>
        {content}
        <BottomNav />
      </div>
    );
  }

  return (
    <DesktopLayout>
      <div style={{ width: '100%', minWidth: 0, background: 'var(--bg, #F5F6FA)', boxSizing: 'border-box', fontFamily: FONT }}>
        {content}
      </div>
    </DesktopLayout>
  );
}
